import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Inbox, RefreshCw, Paperclip, ChevronLeft, Search, X, Clock,
  Trash2, Star, Archive, Reply, Bot, Send, Settings2,
  CheckCircle2, AlertCircle, RotateCcw, Filter, ChevronDown,
  ChevronUp, Loader2, ArrowUpRight, Sparkles, Eye, EyeOff,
  MessageSquare, Zap, MailCheck, StarOff, ArchiveRestore,
} from "lucide-react";
import clsx from "clsx";

import { API_URL } from "@/lib/siteConfig";
const API_BASE = API_URL;

// ── Types ───────────────────────────────────────────────────────────────────

interface InboxMessage {
  id: string;
  message_id: string | null;
  from_address: string;
  to_address: string | null;
  subject: string | null;
  received_at: string;
  is_reply: number;
  processed: number;
  campaign_id: string | null;
  campaign_name: string | null;
  body_text: string | null;
  body_html: string | null;
  attachments: any[] | null;
  starred: number;
  archived: number;
  auto_replied: number;
  auto_reply_id: string | null;
  classification: string | null;
  classification_confidence: string | null;
  bounced_address: string | null;
  bounce_code: string | null;
}

interface AutoReplyEntry {
  id: string;
  campaign_id: string;
  subscriber_id: string;
  subscriber_email?: string;
  subscriber_name?: string;
  campaign_name?: string;
  campaign_subject?: string;
  inbound_text: string | null;
  intent: string | null;
  vertical: string | null;
  classification: string | null;
  generated_reply: string | null;
  status: "sent" | "failed" | "skipped";
  error: string | null;
  message_id: string | null;
  created_at: string;
}

interface AutoReplySettings {
  enabled: boolean;
  instructions: string;
  tone: string;
  signature: string;
  maxPerDay: number;
  excludeDomains: string[];
}

interface InboxStats {
  total: number;
  unread: number;
  starred: number;
  replies: number;
  autoReplied: number;
  archived: number;
  today: number;
  autoRepliesToday: number;
  legitimate: number;
  spam: number;
  bounces: number;
  newsletters: number;
  coldOutreach: number;
  autoAcks: number;
  needsReply: number;
}

// ── API helpers ─────────────────────────────────────────────────────────────

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem("moremail_token");
  return token ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } : { "Content-Type": "application/json" };
}

async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: { ...authHeaders(), ...(options.headers || {}) },
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `API error ${res.status}`);
  }
  return res.json();
}

// ── Utility ─────────────────────────────────────────────────────────────────

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function fullDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString("en-US", {
    weekday: "short", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

function extractName(from: string): string {
  if (!from) return "Unknown";
  const match = from.match(/^"?([^"<]+)"?\s*</);
  if (match) return match[1].trim();
  return from.split("@")[0] || "Unknown";
}

function extractEmail(from: string): string {
  if (!from) return "";
  const match = from.match(/<([^>]+)>/);
  if (match) return match[1];
  return from;
}

function getInitials(name: string): string {
  if (!name) return "?";
  return name.split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "?";
}

const AVATAR_COLORS = [
  "bg-blue-500", "bg-teal", "bg-amber-500", "bg-purple-500",
  "bg-rose-500", "bg-emerald-500", "bg-cyan-500", "bg-orange-500",
];
function avatarColor(email: string): string {
  if (!email) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < email.length; i++) hash = email.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

type FilterTab = "inbox" | "needs-reply" | "starred" | "replies" | "auto-replied" | "spam" | "bounces" | "archived";

const FILTERS: { key: FilterTab; label: string; icon: any }[] = [
  { key: "inbox",        label: "Inbox",        icon: Inbox },
  { key: "needs-reply",  label: "Needs Reply",  icon: MessageSquare },
  { key: "starred",      label: "Starred",      icon: Star },
  { key: "replies",      label: "Replies",      icon: Reply },
  { key: "auto-replied", label: "AI Replied",   icon: Bot },
  { key: "spam",         label: "Spam",         icon: AlertCircle },
  { key: "bounces",      label: "Bounces",      icon: ArrowUpRight },
  { key: "archived",     label: "Archived",     icon: Archive },
];

const TONES = [
  { value: "professional", label: "Professional" },
  { value: "friendly",     label: "Friendly" },
  { value: "casual",       label: "Casual" },
  { value: "formal",       label: "Formal" },
  { value: "custom",       label: "Custom" },
];

// ── Error boundary ─────────────────────────────────────────────────────────
class InboxErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[InboxErrorBoundary]", error, info.componentStack);
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 40, fontFamily: "monospace" }}>
          <h2 style={{ color: "red" }}>Inbox crashed</h2>
          <pre style={{ whiteSpace: "pre-wrap", fontSize: 13 }}>
            {this.state.error.message}
            {"\n\n"}
            {this.state.error.stack}
          </pre>
          <button onClick={() => this.setState({ error: null })} style={{ marginTop: 16, padding: "8px 16px", cursor: "pointer" }}>
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Component ───────────────────────────────────────────────────────────────

function UserInboxPageInner() {
  // View state
  const [view, setView] = useState<"list" | "detail" | "settings" | "log">("list");

  // List state
  const [messages, setMessages] = useState<InboxMessage[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [activeFilter, setActiveFilter] = useState<FilterTab>("inbox");
  const [stats, setStats] = useState<InboxStats | null>(null);

  // Detail state
  const [selected, setSelected] = useState<InboxMessage | null>(null);
  const [selectedAutoReply, setSelectedAutoReply] = useState<AutoReplyEntry | null>(null);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);

  // Settings state
  const [settings, setSettings] = useState<AutoReplySettings | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsDraft, setSettingsDraft] = useState<AutoReplySettings | null>(null);
  const [excludeInput, setExcludeInput] = useState("");

  // Log state
  const [logEntries, setLogEntries] = useState<AutoReplyEntry[]>([]);
  const [logLoading, setLogLoading] = useState(false);
  const [logLoaded, setLogLoaded] = useState(false);
  const [logFilter, setLogFilter] = useState<string>("all");
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);
  const [retrying, setRetrying] = useState<string | null>(null);

  // Toast
  const [toast, setToast] = useState<{ msg: string; type: "ok" | "err" } | null>(null);
  const toastRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function showToast(msg: string, type: "ok" | "err" = "ok") {
    if (toastRef.current) clearTimeout(toastRef.current);
    setToast({ msg, type });
    toastRef.current = setTimeout(() => setToast(null), 3500);
  }

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // ── Load messages ─────────────────────────────────────────────────────────
  const loadMessages = useCallback(async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    else setRefreshing(true);
    try {
      const params = new URLSearchParams({ limit: "50", offset: "0", filter: activeFilter });
      if (searchDebounced) params.set("search", searchDebounced);

      const data = await apiFetch<{ count: number; total: number; messages: InboxMessage[] }>(
        `/user-inbox/messages?${params}`
      );
      setMessages(data.messages || []);
      setTotal(data.total);
    } catch (err: any) {
      showToast(err.message || "Failed to load inbox", "err");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [searchDebounced, activeFilter]);

  // ── Load stats ────────────────────────────────────────────────────────────
  const loadStats = useCallback(async () => {
    try {
      const data = await apiFetch<InboxStats>("/user-inbox/stats");
      setStats(data);
    } catch {}
  }, []);

  useEffect(() => { loadMessages(); }, [loadMessages]);
  useEffect(() => { loadStats(); }, [loadStats]);

  // ── Open detail ───────────────────────────────────────────────────────────
  const openMessage = async (msg: InboxMessage) => {
    setSelected(msg);
    setSelectedAutoReply(null);
    setReplyText("");
    setView("detail");

    try {
      const data = await apiFetch<{ message: InboxMessage; autoReply: AutoReplyEntry | null }>(
        `/user-inbox/messages/${msg.id}`
      );
      setSelected(data.message);
      setSelectedAutoReply(data.autoReply);
      // Mark read locally
      setMessages((prev) => prev.map((m) => m.id === msg.id ? { ...m, processed: 1 } : m));
    } catch {}
  };

  // ── Actions ───────────────────────────────────────────────────────────────
  const toggleStar = async (id: string, current: number, e?: React.MouseEvent) => {
    e?.stopPropagation();
    const newVal = current ? 0 : 1;
    setMessages((prev) => prev.map((m) => m.id === id ? { ...m, starred: newVal } : m));
    if (selected?.id === id) setSelected({ ...selected, starred: newVal });
    try {
      await apiFetch(`/user-inbox/messages/${id}`, { method: "PATCH", body: JSON.stringify({ starred: !current }) });
    } catch { setMessages((prev) => prev.map((m) => m.id === id ? { ...m, starred: current } : m)); }
  };

  const archiveMessage = async (id: string, archive: boolean) => {
    setMessages((prev) => prev.filter((m) => m.id !== id));
    if (selected?.id === id) { setView("list"); setSelected(null); }
    try {
      await apiFetch(`/user-inbox/messages/${id}`, { method: "PATCH", body: JSON.stringify({ archived: archive }) });
      showToast(archive ? "Archived" : "Restored to inbox");
      loadStats();
    } catch (err: any) { showToast(err.message, "err"); loadMessages(false); }
  };

  const deleteMessage = async (id: string) => {
    if (!confirm("Delete this message permanently?")) return;
    setMessages((prev) => prev.filter((m) => m.id !== id));
    if (selected?.id === id) { setView("list"); setSelected(null); }
    try {
      await apiFetch(`/user-inbox/messages/${id}`, { method: "DELETE" });
      showToast("Deleted");
      loadStats();
    } catch (err: any) { showToast(err.message, "err"); loadMessages(false); }
  };

  const sendReply = async () => {
    if (!selected || !replyText.trim()) return;
    setSending(true);
    try {
      await apiFetch(`/user-inbox/messages/${selected.id}/reply`, {
        method: "POST",
        body: JSON.stringify({ text: replyText }),
      });
      showToast("Reply sent!");
      setReplyText("");
    } catch (err: any) {
      showToast(err.message || "Failed to send reply", "err");
    } finally {
      setSending(false);
    }
  };

  // ── Settings ──────────────────────────────────────────────────────────────
  const loadSettings = async () => {
    setSettingsLoading(true);
    try {
      const data = await apiFetch<AutoReplySettings>("/user-inbox/auto-reply-settings");
      setSettings(data);
      setSettingsDraft(data);
    } catch (err: any) {
      showToast("Failed to load settings", "err");
    } finally {
      setSettingsLoading(false);
    }
  };

  const saveSettings = async () => {
    if (!settingsDraft) return;
    setSettingsSaving(true);
    try {
      await apiFetch("/user-inbox/auto-reply-settings", {
        method: "PUT",
        body: JSON.stringify(settingsDraft),
      });
      setSettings(settingsDraft);
      showToast("Auto-reply settings saved");
    } catch (err: any) {
      showToast(err.message || "Failed to save", "err");
    } finally {
      setSettingsSaving(false);
    }
  };

  const addExcludeDomain = () => {
    const d = excludeInput.trim().toLowerCase().replace(/^@/, "");
    if (!d || !settingsDraft) return;
    if (!settingsDraft.excludeDomains.includes(d)) {
      setSettingsDraft({ ...settingsDraft, excludeDomains: [...settingsDraft.excludeDomains, d] });
    }
    setExcludeInput("");
  };

  // ── Log ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (view === "log" && !logLoading && !logLoaded) {
      loadLog();
    }
  }, [view, logLoaded]);

  const loadLog = async () => {
    setLogLoading(true);
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (logFilter !== "all") params.set("status", logFilter);
      const data = await apiFetch<{ log: AutoReplyEntry[] }>(`/user-inbox/auto-reply-log?${params}`);
      setLogEntries(data.log || []);
      setLogLoaded(true);
    } catch (err: any) {
      showToast("Failed to load log", "err");
      setLogLoaded(true);
    } finally {
      setLogLoading(false);
    }
  };

  const retryAutoReply = async (id: string) => {
    setRetrying(id);
    try {
      await apiFetch(`/user-inbox/auto-reply-log/${id}/retry`, { method: "POST" });
      showToast("Auto-reply retried successfully");
      loadLog();
    } catch (err: any) {
      showToast(err.message || "Retry failed", "err");
    } finally {
      setRetrying(null);
    }
  };

  // ── Shared styles ─────────────────────────────────────────────────────────
  const inp = "w-full px-3.5 py-2.5 rounded-xl border border-black/[0.08] bg-white text-ink text-sm focus:outline-none focus:border-coral focus:ring-2 focus:ring-coral/10 transition-all";
  const btnPrimary = "inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark shadow-md shadow-coral/20 disabled:opacity-50 transition-all";
  const btnSecondary = "inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border border-black/[0.08] bg-white text-slate hover:border-black/20 hover:text-ink transition-all";
  const btnGhost = "p-2 rounded-lg text-slate hover:bg-black/[0.04] hover:text-ink transition-colors";

  // ═════════════════════════════════════════════════════════════════════════
  // SETTINGS VIEW
  // ═════════════════════════════════════════════════════════════════════════
  if (view === "settings") {
    if (!settings && !settingsLoading) loadSettings();

    return (
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-paper/90 backdrop-blur border-b border-black/[0.06] px-8 py-5 flex items-center gap-4">
          <button onClick={() => setView("list")} className={btnGhost + " -ml-2"}>
            <ChevronLeft size={20} />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Bot size={20} className="text-coral" /> AI Auto-Reply Settings
            </h1>
            <p className="text-sm text-slate">Control how AI responds to your incoming emails</p>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-8">
          {settingsLoading ? (
            <div className="flex items-center justify-center py-20 text-slate">
              <Loader2 size={20} className="animate-spin mr-2" /> Loading settings…
            </div>
          ) : settingsDraft ? (
            <div className="max-w-2xl space-y-8">
              {/* Master toggle */}
              <div className="bg-white rounded-2xl border border-black/[0.06] p-6">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="font-bold text-ink">Enable Auto-Reply</h3>
                    <p className="text-sm text-slate mt-0.5">
                      When enabled, incoming campaign replies will receive AI-generated responses
                    </p>
                  </div>
                  <button
                    onClick={() => setSettingsDraft({ ...settingsDraft, enabled: !settingsDraft.enabled })}
                    className={clsx(
                      "relative w-12 h-7 rounded-full transition-colors",
                      settingsDraft.enabled ? "bg-coral" : "bg-black/[0.12]"
                    )}
                  >
                    <span className={clsx(
                      "absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform",
                      settingsDraft.enabled ? "translate-x-5" : "translate-x-0"
                    )} />
                  </button>
                </div>

                {stats && (
                  <div className="flex items-center gap-4 text-xs text-slate mt-3 pt-3 border-t border-black/[0.04]">
                    <span className="flex items-center gap-1.5">
                      <Zap size={12} className="text-coral" />
                      {stats.autoRepliesToday} sent today
                    </span>
                    <span className="text-black/10">|</span>
                    <span>Limit: {settingsDraft.maxPerDay}/day</span>
                  </div>
                )}
              </div>

              {settingsDraft.enabled && (
                <>
                  {/* Instructions */}
                  <div className="bg-white rounded-2xl border border-black/[0.06] p-6 space-y-4">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <h3 className="font-bold text-ink flex items-center gap-2">
                        <Sparkles size={16} className="text-coral" /> Reply Instructions
                      </h3>
                      <button
                        type="button"
                        onClick={() => setSettingsDraft({
                          ...settingsDraft,
                          instructions:
                            "Write a short, friendly reply (2-3 sentences max). " +
                            "Mention that HomeLocke has mortgage customers actively asking about mortgages on social media right now, " +
                            "and that they can find these leads at https://homelocke.com/?zip={zip_code}. " +
                            "Do not be pushy — just helpful. Sign off naturally.",
                        })}
                        className="text-xs font-semibold text-coral hover:underline"
                      >
                        Use HomeLocke template
                      </button>
                    </div>
                    <textarea
                      value={settingsDraft.instructions}
                      onChange={(e) => setSettingsDraft({ ...settingsDraft, instructions: e.target.value })}
                      className={inp + " !min-h-[140px]"}
                      rows={6}
                      placeholder="e.g. You are a friendly customer success rep named Sarah. Thank the subscriber for reaching out, answer questions about our product helpfully, and offer to book a demo call. Keep replies concise (under 150 words). Never discuss competitor pricing."
                    />
                    <p className="text-xs text-slate/60">
                      Be specific about persona, tone, what to include/exclude, and length limits. The AI sees the original campaign content and the subscriber's reply for context.
                      {" "}
                      <span className="text-ink/70">
                        Token: <code className="px-1 py-0.5 bg-paper rounded text-[10px]">{"{zip_code}"}</code> is replaced with the 5-digit US ZIP extracted from the inbound email
                        (or an empty string if none was found). Use it inside any URL — e.g.
                        {" "}
                        <code className="text-[10px]">{"https://yoursite.com/?zip={zip_code}"}</code>.
                      </span>
                    </p>
                  </div>

                  {/* Tone + Signature */}
                  <div className="bg-white rounded-2xl border border-black/[0.06] p-6 space-y-5">
                    <h3 className="font-bold text-ink">Tone & Signature</h3>

                    <div>
                      <label className="block text-xs font-semibold text-ink mb-2">Reply Tone</label>
                      <div className="flex flex-wrap gap-2">
                        {TONES.map((t) => (
                          <button
                            key={t.value}
                            onClick={() => setSettingsDraft({ ...settingsDraft, tone: t.value })}
                            className={clsx(
                              "px-4 py-2 rounded-full text-sm font-semibold border transition-all",
                              settingsDraft.tone === t.value
                                ? "bg-coral text-white border-coral shadow-md shadow-coral/20"
                                : "bg-white text-slate border-black/[0.08] hover:border-black/20"
                            )}
                          >
                            {t.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-ink mb-2">
                        Email Signature <span className="text-slate/50 font-normal">(appended to every auto-reply)</span>
                      </label>
                      <textarea
                        value={settingsDraft.signature}
                        onChange={(e) => setSettingsDraft({ ...settingsDraft, signature: e.target.value })}
                        className={inp}
                        rows={3}
                        placeholder={"Best,\nSarah from Acme\nBook a call: https://cal.com/sarah"}
                      />
                    </div>
                  </div>

                  {/* Rate limit + Exclusions */}
                  <div className="bg-white rounded-2xl border border-black/[0.06] p-6 space-y-5">
                    <h3 className="font-bold text-ink">Limits & Exclusions</h3>

                    <div>
                      <label className="block text-xs font-semibold text-ink mb-2">Max auto-replies per day</label>
                      <input
                        type="number"
                        min={1}
                        max={500}
                        value={settingsDraft.maxPerDay}
                        onChange={(e) => setSettingsDraft({ ...settingsDraft, maxPerDay: parseInt(e.target.value) || 50 })}
                        className={inp + " !w-32"}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-ink mb-2">
                        Exclude domains <span className="text-slate/50 font-normal">(never auto-reply to these)</span>
                      </label>
                      <div className="flex gap-2 mb-2">
                        <input
                          value={excludeInput}
                          onChange={(e) => setExcludeInput(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addExcludeDomain())}
                          className={inp + " flex-1"}
                          placeholder="noreply.com"
                        />
                        <button onClick={addExcludeDomain} className={btnSecondary}>Add</button>
                      </div>
                      {settingsDraft.excludeDomains && settingsDraft.excludeDomains.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {settingsDraft.excludeDomains.map((d) => (
                            <span key={d} className="inline-flex items-center gap-1 px-2.5 py-1 bg-paper rounded-lg text-xs text-slate border border-black/[0.06]">
                              @{d}
                              <button
                                onClick={() => setSettingsDraft({
                                  ...settingsDraft,
                                  excludeDomains: settingsDraft.excludeDomains.filter((x) => x !== d),
                                })}
                                className="text-slate/40 hover:text-red-500"
                              >
                                <X size={11} />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Save */}
              <div className="flex items-center gap-3 pt-2">
                <button onClick={saveSettings} disabled={settingsSaving} className={btnPrimary}>
                  {settingsSaving ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
                  Save Settings
                </button>
                <button onClick={() => { setSettingsDraft(settings); }} className={btnSecondary}>
                  Reset
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center py-20 text-slate">Failed to load settings</div>
          )}
        </div>
      </div>
    );
  }

  // ═════════════════════════════════════════════════════════════════════════
  // LOG VIEW
  // ═════════════════════════════════════════════════════════════════════════
  if (view === "log") {

    return (
      <div className="h-full flex flex-col">
        <div className="sticky top-0 z-10 bg-paper/90 backdrop-blur border-b border-black/[0.06] px-8 py-5 flex items-center gap-4">
          <button onClick={() => setView("list")} className={btnGhost + " -ml-2"}>
            <ChevronLeft size={20} />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold flex items-center gap-2">
              <MessageSquare size={20} className="text-coral" /> AI Reply History
            </h1>
            <p className="text-sm text-slate">View and manage all AI-generated replies</p>
          </div>
          <button onClick={loadLog} disabled={logLoading} className={btnSecondary}>
            <RefreshCw size={14} className={logLoading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>

        <div className="p-8 space-y-4">
          {/* Status filter */}
          <div className="flex items-center gap-2">
            {["all", "sent", "failed", "skipped"].map((f) => (
              <button
                key={f}
                onClick={() => { setLogFilter(f); setTimeout(loadLog, 0); }}
                className={clsx(
                  "px-4 py-2 rounded-full text-sm font-semibold border transition-all capitalize",
                  logFilter === f
                    ? "bg-ink text-white border-ink"
                    : "bg-white text-slate border-black/[0.08] hover:border-black/20"
                )}
              >
                {f === "all" ? "All" : f}
              </button>
            ))}
          </div>

          {logLoading ? (
            <div className="flex items-center justify-center py-20 text-slate">
              <Loader2 size={20} className="animate-spin mr-2" /> Loading…
            </div>
          ) : !logEntries || logEntries.length === 0 ? (
            <div className="bg-white rounded-2xl border border-black/[0.06] py-16 text-center">
              <Bot size={36} className="mx-auto text-slate/20 mb-3" />
              <p className="text-slate font-medium">No auto-replies yet</p>
              <p className="text-xs text-slate/50 mt-1">AI replies will appear here once subscribers respond to your campaigns</p>
            </div>
          ) : (
            <div className="space-y-2">
              {(logEntries || []).map((entry) => (
                <div key={entry.id} className="bg-white rounded-2xl border border-black/[0.06] overflow-hidden">
                  <button
                    onClick={() => setExpandedLogId(expandedLogId === entry.id ? null : entry.id)}
                    className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-black/[0.01] transition-colors"
                  >
                    {entry.status === "sent" ? (
                      <CheckCircle2 size={16} className="text-emerald-500 shrink-0" />
                    ) : entry.status === "failed" ? (
                      <AlertCircle size={16} className="text-red-500 shrink-0" />
                    ) : (
                      <EyeOff size={16} className="text-amber-500 shrink-0" />
                    )}

                    <div className={clsx(
                      "w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0",
                      avatarColor(entry.subscriber_email || entry.subscriber_id)
                    )}>
                      {getInitials(entry.subscriber_name || entry.subscriber_email || "?")}
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-ink truncate">
                        {entry.subscriber_name || entry.subscriber_email || entry.subscriber_id}
                      </p>
                      <p className="text-xs text-slate truncate">
                        {entry.campaign_name || entry.campaign_subject || entry.campaign_id}
                      </p>
                    </div>

                    {entry.intent && (
                      <span className="text-[10px] font-semibold text-slate bg-black/[0.04] px-1.5 py-0.5 rounded shrink-0">
                        {entry.intent.replace(/_/g, " ")}
                      </span>
                    )}

                    {entry.vertical && (
                      <span className="text-[10px] font-semibold text-teal bg-teal-light px-1.5 py-0.5 rounded shrink-0">
                        {entry.vertical}
                      </span>
                    )}

                    <span className={clsx(
                      "text-[10px] font-bold uppercase px-2 py-0.5 rounded-full shrink-0",
                      entry.status === "sent" ? "text-emerald-600 bg-emerald-50" :
                      entry.status === "failed" ? "text-red-600 bg-red-50" :
                      "text-amber-600 bg-amber-50"
                    )}>
                      {entry.status}
                    </span>

                    <span className="text-xs text-slate whitespace-nowrap shrink-0">
                      {formatDate(entry.created_at)}
                    </span>

                    {expandedLogId === entry.id ? <ChevronUp size={14} className="text-slate shrink-0" /> : <ChevronDown size={14} className="text-slate shrink-0" />}
                  </button>

                  {expandedLogId === entry.id && (
                    <div className="px-5 pb-5 space-y-3 border-t border-black/[0.04]">
                      {/* Intent / Vertical / Classification metadata */}
                      {(entry.intent || entry.vertical || entry.classification) && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {entry.intent && (
                            <span className={clsx(
                              "text-[10px] font-semibold px-2 py-1 rounded-lg",
                              entry.intent === "quote_request" ? "text-blue-700 bg-blue-50" :
                              entry.intent === "decline" ? "text-red-600 bg-red-50" :
                              entry.intent === "interested" ? "text-green-700 bg-green-50" :
                              entry.intent === "question" ? "text-purple-600 bg-purple-50" :
                              entry.intent === "auto_ack" ? "text-slate bg-black/[0.04]" :
                              "text-slate bg-black/[0.04]"
                            )}>
                              Intent: {entry.intent.replace(/_/g, " ")}
                            </span>
                          )}
                          {entry.vertical && (
                            <span className="text-[10px] font-semibold text-teal bg-teal-light px-2 py-1 rounded-lg">
                              Vertical: {entry.vertical}
                            </span>
                          )}
                          {entry.classification && (
                            <span className="text-[10px] font-semibold text-ink bg-black/[0.04] px-2 py-1 rounded-lg">
                              Class: {entry.classification}
                            </span>
                          )}
                        </div>
                      )}
                      {entry.inbound_text && (
                        <div className="mt-3">
                          <p className="text-[11px] font-bold uppercase tracking-wider text-slate mb-1.5">Subscriber wrote</p>
                          <div className="bg-paper rounded-xl px-4 py-3 text-sm text-ink whitespace-pre-wrap leading-relaxed">
                            {entry.inbound_text}
                          </div>
                        </div>
                      )}
                      {entry.generated_reply && (
                        <div>
                          <p className="text-[11px] font-bold uppercase tracking-wider text-slate mb-1.5 flex items-center gap-1.5">
                            <Bot size={11} className="text-coral" /> AI replied
                          </p>
                          <div className="bg-coral/[0.04] border border-coral/10 rounded-xl px-4 py-3 text-sm text-ink whitespace-pre-wrap leading-relaxed">
                            {entry.generated_reply}
                          </div>
                        </div>
                      )}
                      {entry.error && (
                        <div>
                          <p className="text-[11px] font-bold uppercase tracking-wider text-red-500 mb-1.5">Error</p>
                          <p className="text-xs text-red-600 bg-red-50 rounded-xl px-4 py-3">{entry.error}</p>
                        </div>
                      )}
                      {entry.status === "failed" && (
                        <button
                          onClick={() => retryAutoReply(entry.id)}
                          disabled={retrying === entry.id}
                          className={btnPrimary + " !text-xs !px-4 !py-2 mt-1"}
                        >
                          {retrying === entry.id ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />}
                          Retry
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ═════════════════════════════════════════════════════════════════════════
  // DETAIL VIEW
  // ═════════════════════════════════════════════════════════════════════════
  if (view === "detail" && selected) {
    const attachments = (() => {
      if (!selected.attachments) return [];
      if (typeof selected.attachments === "string") {
        try { return JSON.parse(selected.attachments); } catch { return []; }
      }
      return Array.isArray(selected.attachments) ? selected.attachments : [];
    })();

    return (
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-paper/90 backdrop-blur border-b border-black/[0.06] px-8 py-4 flex items-center gap-3">
          <button onClick={() => { setView("list"); setSelected(null); }} className={btnGhost + " -ml-2"}>
            <ChevronLeft size={20} />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold truncate">{selected.subject || "(no subject)"}</h1>
            <p className="text-sm text-slate truncate">
              {extractName(selected.from_address)} &lt;{extractEmail(selected.from_address)}&gt;
            </p>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => toggleStar(selected.id, selected.starred)} className={btnGhost} title={selected.starred ? "Unstar" : "Star"}>
              {selected.starred ? <Star size={18} className="text-amber-400 fill-amber-400" /> : <Star size={18} />}
            </button>
            <button onClick={() => archiveMessage(selected.id, !selected.archived)} className={btnGhost} title={selected.archived ? "Unarchive" : "Archive"}>
              {selected.archived ? <ArchiveRestore size={18} /> : <Archive size={18} />}
            </button>
            <button onClick={() => deleteMessage(selected.id)} className="p-2 rounded-lg text-red-400 hover:bg-red-50 transition-colors" title="Delete">
              <Trash2 size={18} />
            </button>
          </div>
        </div>

        {/* Message header */}
        <div className="px-8 py-4 border-b border-black/[0.06] bg-white">
          <div className="flex items-start gap-4">
            <div className={clsx(
              "w-11 h-11 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0",
              avatarColor(extractEmail(selected.from_address))
            )}>
              {getInitials(extractName(selected.from_address))}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="font-bold text-ink">{extractName(selected.from_address)}</span>
                <span className="text-xs text-slate">{extractEmail(selected.from_address)}</span>
                <span className="text-xs text-slate/50 ml-auto flex items-center gap-1">
                  <Clock size={11} /> {fullDate(selected.received_at)}
                </span>
              </div>
              <div className="text-xs text-slate mt-0.5">To: {selected.to_address || "—"}</div>

              <div className="flex items-center gap-2 mt-2 flex-wrap">
                {selected.campaign_name && (
                  <span className="text-[10px] font-semibold text-teal bg-teal-light px-2 py-0.5 rounded-full">
                    Campaign: {selected.campaign_name}
                  </span>
                )}
                {selected.is_reply ? (
                  <span className="text-[10px] font-semibold text-teal bg-teal-light px-2 py-0.5 rounded-full flex items-center gap-1">
                    <Reply size={10} /> Reply
                  </span>
                ) : null}
                {selected.auto_replied ? (
                  <span className="text-[10px] font-semibold text-coral bg-coral-light px-2 py-0.5 rounded-full flex items-center gap-1">
                    <Bot size={10} /> AI Replied
                  </span>
                ) : null}
              </div>

              {attachments && attachments.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {attachments.map((a: any, i: number) => (
                    <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-paper rounded-lg text-xs text-slate border border-black/[0.06]">
                      <Paperclip size={12} />
                      {a.filename || "attachment"} {a.size ? `(${(a.size / 1024).toFixed(1)}KB)` : ""}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Message body */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-8 space-y-6">
            {/* Email content */}
            <div className="bg-white rounded-2xl border border-black/[0.06] overflow-hidden max-w-3xl">
              {selected.body_html ? (
                <iframe
                  srcDoc={`<!DOCTYPE html><html><head><style>body{font-family:-apple-system,system-ui,sans-serif;font-size:14px;color:#333;line-height:1.6;margin:0;padding:24px;}a{color:#0A8F7F;}img{max-width:100%;height:auto;}</style></head><body>${selected.body_html}</body></html>`}
                  className="w-full border-0"
                  style={{ minHeight: "300px" }}
                  sandbox="allow-same-origin"
                  title="Email content"
                  onLoad={(e) => {
                    const iframe = e.target as HTMLIFrameElement;
                    if (iframe.contentDocument) {
                      iframe.style.height = iframe.contentDocument.body.scrollHeight + 40 + "px";
                    }
                  }}
                />
              ) : (
                <div className="p-6">
                  <pre className="whitespace-pre-wrap text-sm text-ink font-sans leading-relaxed">
                    {selected.body_text || "(empty message)"}
                  </pre>
                </div>
              )}
            </div>

            {/* Auto-reply display */}
            {selectedAutoReply && (
              <div className="max-w-3xl">
                <div className="flex items-center gap-2 mb-2">
                  <Bot size={14} className="text-coral" />
                  <span className="text-xs font-bold uppercase tracking-wider text-slate">
                    AI Auto-Reply
                    <span className={clsx(
                      "ml-2 px-1.5 py-0.5 rounded text-[10px]",
                      selectedAutoReply.status === "sent" ? "bg-emerald-50 text-emerald-600" :
                      selectedAutoReply.status === "failed" ? "bg-red-50 text-red-600" :
                      "bg-amber-50 text-amber-600"
                    )}>
                      {selectedAutoReply.status}
                    </span>
                  </span>
                  <span className="text-[11px] text-slate/50 ml-auto">{fullDate(selectedAutoReply.created_at)}</span>
                </div>
                <div className="bg-coral/[0.03] border border-coral/10 rounded-2xl p-6">
                  {selectedAutoReply.generated_reply ? (
                    <pre className="whitespace-pre-wrap text-sm text-ink font-sans leading-relaxed">
                      {selectedAutoReply.generated_reply}
                    </pre>
                  ) : selectedAutoReply.error ? (
                    <p className="text-sm text-red-600">{selectedAutoReply.error}</p>
                  ) : (
                    <p className="text-sm text-slate">No reply generated</p>
                  )}
                </div>
              </div>
            )}

            {/* Manual reply composer */}
            <div className="max-w-3xl">
              <div className="flex items-center gap-2 mb-2">
                <Reply size={14} className="text-teal" />
                <span className="text-xs font-bold uppercase tracking-wider text-slate">Reply</span>
              </div>
              <div className="bg-white rounded-2xl border border-black/[0.06] overflow-hidden">
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  className="w-full px-6 py-4 text-sm text-ink border-0 focus:outline-none resize-none"
                  rows={5}
                  placeholder="Write a reply…"
                />
                <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-black/[0.04] bg-paper/50">
                  <button
                    onClick={sendReply}
                    disabled={sending || !replyText.trim()}
                    className={btnPrimary}
                  >
                    {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                    Send Reply
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ═════════════════════════════════════════════════════════════════════════
  // LIST VIEW (default)
  // ═════════════════════════════════════════════════════════════════════════
  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-paper/90 backdrop-blur border-b border-black/[0.06] px-8 py-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Inbox size={22} className="text-coral" /> My Inbox
            </h1>
            <p className="text-sm text-slate">
              {stats ? (
                <>
                  {stats.unread} unread · {stats.today} today
                  {stats.autoRepliesToday > 0 && <> · <Bot size={12} className="inline text-coral" /> {stats.autoRepliesToday} AI replies today</>}
                </>
              ) : (
                <>{total} message{total !== 1 ? "s" : ""}</>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => { setLogLoaded(false); setView("log"); }} className={btnSecondary} title="AI Reply History">
              <MessageSquare size={15} />
              <span className="hidden sm:inline">AI Log</span>
            </button>
            <button onClick={() => { setView("settings"); loadSettings(); }} className={btnSecondary} title="Auto-Reply Settings">
              <Settings2 size={15} />
              <span className="hidden sm:inline">Settings</span>
            </button>
            <button onClick={() => loadMessages(false)} disabled={refreshing} className={btnPrimary}>
              <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>

        {/* Filters + Search */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1 bg-white rounded-xl border border-black/[0.06] p-1">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setActiveFilter(f.key)}
                className={clsx(
                  "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all",
                  activeFilter === f.key
                    ? "bg-ink text-white shadow-sm"
                    : "text-slate hover:text-ink hover:bg-black/[0.03]"
                )}
              >
                <f.icon size={13} />
                {f.label}
                {f.key === "inbox" && stats?.unread ? (
                  <span className="bg-coral text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                    {stats.unread}
                  </span>
                ) : null}
                {f.key === "needs-reply" && stats?.needsReply ? (
                  <span className="bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                    {stats.needsReply}
                  </span>
                ) : null}
                {f.key === "spam" && stats?.spam ? (
                  <span className="bg-red-400 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                    {stats.spam}
                  </span>
                ) : null}
                {f.key === "bounces" && stats?.bounces ? (
                  <span className="bg-orange-400 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                    {stats.bounces}
                  </span>
                ) : null}
              </button>
            ))}
          </div>

          <div className="relative flex-1 max-w-sm ml-auto">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search emails…"
              className="w-full pl-9 pr-8 py-2 rounded-xl border border-black/[0.08] text-sm bg-white focus:outline-none focus:border-coral focus:ring-2 focus:ring-coral/10 transition-all"
            />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate hover:text-ink">
                <X size={14} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Message list */}
      <div className="flex-1 overflow-y-auto p-8">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate">
            <Loader2 size={20} className="animate-spin mr-2" /> Loading inbox…
          </div>
        ) : !messages || messages.length === 0 ? (
          <div className="bg-white rounded-2xl border border-black/[0.06] py-20 text-center">
            <Inbox size={40} className="mx-auto text-slate/20 mb-3" />
            <p className="text-slate font-medium">
              {search ? "No messages match your search" : `No ${activeFilter === "inbox" ? "" : activeFilter + " "}messages`}
            </p>
            <p className="text-xs text-slate/50 mt-1">
              {activeFilter === "inbox" && !search && "Incoming emails will appear here"}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-black/[0.06] overflow-hidden divide-y divide-black/[0.04]">
            {(messages || []).map((msg) => (
              <div
                key={msg.id}
                onClick={() => openMessage(msg)}
                className={clsx(
                  "flex items-center gap-3 px-5 py-3.5 cursor-pointer hover:bg-black/[0.015] transition-colors group",
                  !msg.processed && "bg-blue-50/30"
                )}
              >
                {/* Star */}
                <button
                  onClick={(e) => toggleStar(msg.id, msg.starred, e)}
                  className="shrink-0 p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                  style={msg.starred ? { opacity: 1 } : undefined}
                >
                  {msg.starred ? (
                    <Star size={15} className="text-amber-400 fill-amber-400" />
                  ) : (
                    <Star size={15} className="text-slate/30 hover:text-amber-400" />
                  )}
                </button>

                {/* Avatar */}
                <div className={clsx(
                  "w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0",
                  avatarColor(extractEmail(msg.from_address))
                )}>
                  {getInitials(extractName(msg.from_address))}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={clsx("text-sm truncate", !msg.processed ? "font-bold" : "font-semibold")}>
                      {extractName(msg.from_address)}
                    </span>
                    {!msg.processed && <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />}
                  </div>
                  <div className={clsx("text-sm truncate", !msg.processed && "font-semibold")}>
                    {msg.subject || "(no subject)"}
                  </div>
                  <div className="text-xs text-slate truncate mt-0.5">
                    {(msg.body_text || "").slice(0, 120)}
                  </div>
                </div>

                {/* Badges */}
                <div className="flex items-center gap-1.5 shrink-0">
                  {msg.classification === "bounce" ? (
                    <span className="text-[10px] font-semibold text-orange-700 bg-orange-50 px-1.5 py-0.5 rounded">Bounce</span>
                  ) : msg.classification === "spam" ? (
                    <span className="text-[10px] font-semibold text-red-600 bg-red-50 px-1.5 py-0.5 rounded">Spam</span>
                  ) : msg.classification === "newsletter" || msg.classification === "notification" ? (
                    <span className="text-[10px] font-semibold text-slate bg-black/[0.04] px-1.5 py-0.5 rounded">
                      {msg.classification === "newsletter" ? "Newsletter" : "Notification"}
                    </span>
                  ) : msg.classification === "auto_ack" ? (
                    <span className="text-[10px] font-semibold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">Auto-ack</span>
                  ) : msg.classification === "cold_outreach" ? (
                    <span className="text-[10px] font-semibold text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">Cold pitch</span>
                  ) : msg.classification === "legitimate" ? (
                    <span className="text-[10px] font-semibold text-green-700 bg-green-50 px-1.5 py-0.5 rounded">Legitimate</span>
                  ) : null}
                  {msg.is_reply ? (
                    <span className="text-[10px] font-semibold text-teal bg-teal-light px-1.5 py-0.5 rounded">Reply</span>
                  ) : null}
                  {msg.auto_replied ? (
                    <span className="text-[10px] font-semibold text-coral bg-coral-light px-1.5 py-0.5 rounded flex items-center gap-0.5">
                      <Bot size={9} /> AI
                    </span>
                  ) : null}
                  {msg.bounced_address ? (
                    <span className="text-[10px] text-slate" title={`Bounced: ${msg.bounced_address} (${msg.bounce_code || "unknown"})`}>
                      → {msg.bounced_address.split("@")[0]}
                    </span>
                  ) : null}
                </div>

                {/* Time */}
                <div className="text-xs text-slate whitespace-nowrap shrink-0 w-14 text-right">
                  {formatDate(msg.received_at)}
                </div>

                {/* Quick actions */}
                <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                  <button
                    onClick={(e) => { e.stopPropagation(); archiveMessage(msg.id, !msg.archived); }}
                    className="p-1.5 rounded-lg text-slate/50 hover:text-ink hover:bg-black/[0.04]"
                    title={msg.archived ? "Unarchive" : "Archive"}
                  >
                    {msg.archived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteMessage(msg.id); }}
                    className="p-1.5 rounded-lg text-slate/50 hover:text-red-500 hover:bg-red-50"
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Toast */}
      {toast && (
        <div className={clsx(
          "fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-2xl shadow-xl text-sm font-semibold flex items-center gap-2 transition-all",
          toast.type === "ok"
            ? "bg-ink text-white"
            : "bg-red-600 text-white"
        )}>
          {toast.type === "ok" ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
          {toast.msg}
        </div>
      )}
    </div>
  );
}

export default function UserInboxPage() {
  return (
    <InboxErrorBoundary>
      <UserInboxPageInner />
    </InboxErrorBoundary>
  );
}